import { computed, toValue, type MaybeRefOrGetter } from "vue"
import { useQuery } from "@tanstack/vue-query"
import type { MemberIchraPaymentAccount } from "@vitable/asclepius-api-client-ts"
import useAsclepius from "@common/composables/api/asclepius"

export const healthWalletKeys = {
  all: ["health-wallet"] as const,
  choicePaymentAccounts: (memberId: string) => [...healthWalletKeys.all, "choice-payment-accounts", memberId] as const
}

/** What the CHOICE card needs from a payment account. Bank numbers and balances never reach the query cache. */
export type ChoicePaymentAccount = Pick<MemberIchraPaymentAccount, "id" | "employer_name" | "card_provisioned_in">

/** `memberId` is the prefixed public id (`mbr_…`); the query waits until it is known. */
export function useChoicePaymentAccounts(memberId: MaybeRefOrGetter<string | undefined>) {
  const { getMembersApi } = useAsclepius()

  const query = useQuery({
    queryKey: computed(() => healthWalletKeys.choicePaymentAccounts(toValue(memberId) ?? "")),
    enabled: computed(() => Boolean(toValue(memberId))),
    // Every request reads each account from Ambient, and the employer and card state rarely change.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async ({ queryKey }): Promise<Array<ChoicePaymentAccount>> => {
      const response = await getMembersApi().listMemberIchraPaymentAccounts({ memberId: queryKey[2] })
      return response.data.data.map(({ id, employer_name, card_provisioned_in }) => ({
        id,
        employer_name,
        card_provisioned_in
      }))
    }
  })

  const accounts = computed<Array<ChoicePaymentAccount>>(() => query.data.value ?? [])

  return { ...query, accounts }
}
